'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useLanguage } from '@/contexts/LanguageContext'
import { serviceAreaTranslations } from '@/translations/serviceArea'

const serviceAreas = [
  {
    name: 'Barry',
    postcodes: ['CF62'],
    level: 'Premium',
    color: 'bg-gray-800'
  },
  {
    name: 'Penarth',
    postcodes: ['CF64'],
    level: 'Premium',
    color: 'bg-gray-800'
  },
  {
    name: 'Dinas Powys',
    postcodes: ['CF64'],
    level: 'Premium',
    color: 'bg-gray-800'
  },
  {
    name: 'Vale of Glamorgan',
    postcodes: ['CF61', 'CF62', 'CF63', 'CF64', 'CF71'],
    level: 'Premium',
    color: 'bg-gray-800'
  },
  {
    name: 'Cardiff',
    postcodes: ['CF10', 'CF11', 'CF14', 'CF15', 'CF23', 'CF24'],
    level: 'Standard',
    color: 'bg-gray-800'
  },
  {
    name: 'Bridgend',
    postcodes: ['CF31', 'CF32', 'CF33', 'CF34', 'CF35'],
    level: 'Standard',
    color: 'bg-gray-800'
  },
  {
    name: 'Swansea',
    postcodes: ['SA1', 'SA2', 'SA3', 'SA4', 'SA5', 'SA6', 'SA7', 'SA8', 'SA9'],
    level: 'Standard',
    color: 'bg-gray-800'
  },
  {
    name: 'Porthcawl',
    postcodes: ['CF36'],
    level: 'Standard',
    color: 'bg-gray-800'
  }
]

export default function ServiceArea() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [postcode, setPostcode] = useState('')
  const [result, setResult] = useState<{
    area: typeof serviceAreas[0] | null;
    message: string;
  } | null>(null)

  const { language } = useLanguage()
  const t = serviceAreaTranslations[language]

  const getServiceLevelTranslation = (level: string) => {
    if (level === 'Premium') {
      return t.serviceLevels.premium.title
    } else if (level === 'Standard') {
      return t.serviceLevels.standard.title
    }
    return level
  }

  const checkPostcode = () => {
    const formattedPostcode = postcode.toUpperCase().trim()
    const area = serviceAreas.find(area => 
      area.postcodes.some(code => formattedPostcode.startsWith(code))
    )

    if (area) {
      setResult({
        area,
        message: t.form.success
          .replace('{area}', area.name)
          .replace('{level}', getServiceLevelTranslation(area.level))
      })
    } else {
      setResult({
        area: null,
        message: t.form.error
      })
    }
  }

  return (
    <section id="service-area" className="py-24 bg-gradient-to-br from-gray-50/30 via-white to-gray-50/20" aria-labelledby="service-area-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 id="service-area-heading" className="text-3xl font-bold text-gray-900 sm:text-4xl">
            {t.title}
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <motion.div
          ref={ref}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-16"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t.form.title}</h3>
              <p className="mt-2 text-gray-600">
                {t.form.description}
              </p>
              <div className="mt-4 flex flex-col gap-2 md:flex-row">
                <input
                  type="text"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                  onFocus={() => {
                    if (result && result.area === null) setResult(null);
                  }}
                  placeholder={t.form.placeholder}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 shadow-sm focus:border-gray-500 focus:ring-2 focus:ring-gray-200 placeholder-gray-500 text-gray-900 transition-all duration-200 text-sm md:text-base"
                />
                <button
                  onClick={checkPostcode}
                  className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-emerald-700 hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 md:ml-2 mt-1 md:mt-0"
                >
                  {t.form.button}
                </button>
              </div>
              {result && (
                <div className={`mt-4 p-4 rounded-lg ${
                  result.area ? 'bg-gray-50 text-gray-800 border border-gray-200' : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  <p className="text-sm font-medium">{result.message}</p>
                  {result.area && (
                    <div className="mt-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        result.area.level === 'Premium'
                          ? 'bg-emerald-700 text-white'
                          : 'bg-gray-800 text-white'
                      }`}>
                        {getServiceLevelTranslation(result.area.level)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t.serviceLevels.title}</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <div className="flex items-center">
                    <span className="h-3 w-3 rounded-full bg-gray-800" />
                    <span className="ml-2 text-sm font-medium text-gray-900">{t.serviceLevels.premium.title}</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                    {t.serviceLevels.premium.description}
                  </p>
                </div>
                <div>
                  <div className="flex items-center">
                    <span className="h-3 w-3 rounded-full bg-gray-800" />
                    <span className="ml-2 text-sm font-medium text-gray-900">{t.serviceLevels.standard.title}</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                    {t.serviceLevels.standard.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

/* 
Image URLs to download from Unsplash:
South Wales Map: Please find a suitable map image for South Wales.

Note: All images from Unsplash are free to use for commercial and noncommercial purposes.
No attribution is required, but it's appreciated.
*/ 